// Airline names from the two-letter code at the start of a flight number ("AA 4451" ->
// American Airlines), and the "operated by" airline on codeshares. Not exhaustive: an
// unknown code just shows the flight number. Extend freely.

import type { Segment } from './types.ts';

export const AIRLINES: Record<string, string> = {
  // North America
  AA: 'American Airlines',
  DL: 'Delta',
  UA: 'United',
  B6: 'JetBlue',
  WN: 'Southwest',
  AS: 'Alaska Airlines',
  NK: 'Spirit',
  F9: 'Frontier',
  G4: 'Allegiant',
  HA: 'Hawaiian Airlines',
  SY: 'Sun Country',
  MX: 'Breeze',
  AC: 'Air Canada',
  WS: 'WestJet',
  PD: 'Porter',
  AM: 'Aeroméxico',
  // Europe
  BA: 'British Airways',
  VS: 'Virgin Atlantic',
  AF: 'Air France',
  KL: 'KLM',
  LH: 'Lufthansa',
  LX: 'Swiss',
  OS: 'Austrian',
  SN: 'Brussels Airlines',
  IB: 'Iberia',
  VY: 'Vueling',
  TP: 'TAP Air Portugal',
  AZ: 'ITA Airways',
  EI: 'Aer Lingus',
  SK: 'SAS',
  AY: 'Finnair',
  LO: 'LOT',
  TK: 'Turkish Airlines',
  FR: 'Ryanair',
  U2: 'easyJet',
  W6: 'Wizz Air',
  // Middle East and Africa
  EK: 'Emirates',
  QR: 'Qatar Airways',
  EY: 'Etihad',
  SV: 'Saudia',
  LY: 'El Al',
  ET: 'Ethiopian Airlines',
  MS: 'EgyptAir',
  // Asia and Pacific
  JL: 'Japan Airlines',
  NH: 'ANA',
  KE: 'Korean Air',
  OZ: 'Asiana',
  CX: 'Cathay Pacific',
  SQ: 'Singapore Airlines',
  TR: 'Scoot',
  MH: 'Malaysia Airlines',
  AK: 'AirAsia',
  D7: 'AirAsia X',
  OD: 'Batik Air Malaysia',
  TG: 'Thai Airways',
  FD: 'Thai AirAsia',
  VN: 'Vietnam Airlines',
  VJ: 'VietJet',
  QH: 'Bamboo Airways',
  PR: 'Philippine Airlines',
  '5J': 'Cebu Pacific',
  GA: 'Garuda Indonesia',
  CI: 'China Airlines',
  BR: 'EVA Air',
  CA: 'Air China',
  MU: 'China Eastern',
  CZ: 'China Southern',
  AI: 'Air India',
  '6E': 'IndiGo',
  QF: 'Qantas',
  VA: 'Virgin Australia',
  NZ: 'Air New Zealand',
  // Latin America
  LA: 'LATAM',
  AV: 'Avianca',
  CM: 'Copa Airlines',
};

/** "AA" from "AA 4451", "aa4451" or "AA-4451". */
export function airlineCode(flightNumber: string | undefined): string | undefined {
  const m = flightNumber?.trim().toUpperCase().match(/^([A-Z0-9]{2})[\s-]?\d{1,4}[A-Z]?$/);
  return m && /[A-Z]/.test(m[1]) ? m[1] : undefined;
}

/** A code ("JL") or a name as printed ("Japan Airlines") -> the display name. */
export function airlineName(codeOrName: string | undefined): string | undefined {
  const v = codeOrName?.trim();
  if (!v) return undefined;
  return AIRLINES[v.toUpperCase()] ?? v;
}

/** "JetBlue", "JetBlue Airways", "American" or "B6" -> the two-letter code, when known. */
export function airlineCodeFor(name: string | undefined): string | undefined {
  const v = name?.trim().toLowerCase();
  if (!v) return undefined;
  if (v.length === 2 && AIRLINES[v.toUpperCase()]) return v.toUpperCase();
  if (v.length < 4) return undefined;
  const hit = Object.entries(AIRLINES).find(([, n]) => {
    const known = n.toLowerCase();
    return v === known || v.startsWith(known) || known.startsWith(v);
  });
  return hit?.[0];
}

/** The airline selling the flight: what the email said, else the flight number's code. */
export function flightAirline(s: Pick<Segment, 'provider' | 'number'>): string | undefined {
  if (s.provider?.trim()) return airlineName(s.provider);
  const code = airlineCode(s.number);
  return code ? AIRLINES[code] : undefined;
}

/** The airline actually flying it, when it differs from the one selling it. */
export function operatingAirline(s: Pick<Segment, 'provider' | 'number' | 'details'>): string | undefined {
  const op = airlineName(s.details?.['Operated by']);
  if (!op) return undefined;
  const seller = flightAirline(s);
  return seller && seller.toLowerCase() === op.toLowerCase() ? undefined : op;
}
